import type { Traducao } from '../tipos';

/**
 * Textos de `imagens` — a tela de Imagens de Máquinas (acervo de fotos por
 * pasta), dividida em quatro blocos:
 *
 * - `acervo`      — casca da tela, abas, métricas, cartões e galeria;
 * - `upload`      — envio de fotos e importação de ZIP;
 * - `encaixe`     — fluxo de encaixar as fotos numa apreciação com IA;
 * - `revisaoLegado` — acervo migrado e a revisão foto a foto.
 *
 * Nome de pasta, nome de arquivo e nome de máquina vêm do disco/banco e
 * aparecem como estão — nunca passam por aqui.
 */
export const imagens: Traducao = {
  pt: {
    acervo: {
      titulo: 'Imagens de Máquinas',
      subtitulo: 'Organize as fotos por área e acompanhe o status da apreciação de cada máquina.',
      subtituloLegado:
        'Acervo migrado: entre na máquina e aprove ou reprove cada foto para saber se é preciso voltar a campo.',
      semProjeto: 'Selecione um projeto para visualizar os uploads de imagens.',
      semArea: 'Sem área',
      visao: {
        porArea: 'Por área',
        porAreaTitulo: 'Agrupar por área',
        lista: 'Lista',
        listaTitulo: 'Mostrar todas as pastas em lista',
      },
      aba: {
        atuais: 'Atuais',
        legado: 'Legado',
        legadoTitulo: 'Fotos antigas migradas, em revisão foto a foto',
      },
      busca: {
        placeholder: 'Buscar pasta, máquina, área ou status...',
        novaPasta: 'Nova pasta...',
        novaMaquinaLegado: 'Nova máquina do legado...',
      },
      filtro: {
        pendentes: 'Pendentes',
        pendentesTitulo:
          "Mostrar apenas pastas sem apreciação e sem status 'A fazer' (canceladas e não NR12 ficam de fora)",
        aRevisar: 'A revisar',
        aRevisarTitulo: 'Mostrar apenas máquinas com foto ainda sem veredito',
      },
      metricas: {
        totalMaquinas: 'Total de máquinas',
        canceladasSufixo: ' · {{quantidade}} cancelada(s)',
        naoNr12Sufixo: ' · {{quantidade}} não NR12',
        comApreciacao: 'Com apreciação',
        semApreciacao: 'Sem apreciação',
      },
      grupo: {
        maquinas: '{{quantidade}} máquina(s)',
      },
      vazio: {
        semPastas: 'Nenhuma pasta com imagens neste projeto.',
        semPendentes:
          'Nenhuma pasta pendente — todas têm apreciação, estão marcadas como "A fazer" ou fora do escopo.',
      },
      semProjetoLista: {
        titulo: 'Pastas sem projeto',
        descricao: 'Pastas antigas encontradas fora da separação por projeto.',
      },
      status: {
        aFazer: 'A fazer',
        semFotoMaquina: 'Sem foto de máquina',
        semFotoPainel: 'Sem foto de painel',
        incompleto: 'Incompleto',
        naoNr12: 'Não é NR12',
        cancelado: 'Cancelado',
        definir: 'Definir status da pasta',
        alterar: 'Status: {{status}} (clique para alterar)',
        remover: 'Remover status',
      },
      levantamento: {
        rotulo: 'Levantamento: {{data}}',
        captura: 'Data registrada pelo app no momento da foto',
        upload: 'Data em que a primeira foto da pasta chegou ao servidor',
        inferida: 'Estimada pelo arquivo mais antigo da pasta — pode atrasar dias em relação à visita',
        importacao: 'Pasta de acervo importado: esta é a data do import, não a do levantamento',
      },
      card: {
        fotos: '{{quantidade}} foto(s)',
        videosSufixo: ' · {{quantidade}} vídeo(s)',
        semFotos: 'Sem fotos ainda',
        renomear: 'Renomear pasta',
        renomearPlaceholder: 'Novo nome da pasta...',
        salvarNome: 'Salvar nome',
        definirArea: 'Definir área',
        areaPlaceholder: 'Nome da área...',
        salvarArea: 'Salvar área',
        adicionarFotos: 'Adicionar fotos',
        baixarZip: 'Baixar ZIP da pasta',
        pastaSemFotos: 'Pasta sem fotos',
        apagarPasta: 'Apagar pasta',
        apreciacao: 'Apreciação',
        semApreciacao: 'Sem apreciação',
        variasApreciacoes: '{{quantidade}} apreciações',
        editarApreciacao: 'Editar apreciação',
        gerenciarApreciacoes: 'Ver/gerenciar apreciações',
        criarApreciacao: 'Criar apreciação',
        vincularApreciacao: 'Vincular apreciação',
        alterarApreciacao: 'Alterar/desvincular apreciação',
      },
      galeria: {
        vazioTitulo: 'Esta pasta ainda não tem fotos.',
        vazioDica: 'Use o botão de adicionar fotos no cartão da pasta para enviar imagens.',
        abrirVideo: 'Abrir vídeo e capturar quadro',
        abrirFoto: 'Abrir em alta qualidade',
        apagarFoto: 'Apagar foto',
      },
      confirmar: {
        apagarPasta: 'Apagar a pasta "{{nome}}" com {{fotos}} foto(s)? Esta ação não pode ser desfeita.',
        apagarFoto: 'Apagar a foto "{{nome}}"? Esta ação não pode ser desfeita.',
      },
      erro: {
        carregar: 'Falha ao carregar imagens',
        semProjeto: 'Nenhum projeto selecionado.',
        criarPasta: 'Falha ao criar pasta',
        apagarPasta: 'Falha ao apagar pasta',
        apagarFoto: 'Falha ao apagar foto',
        salvarArea: 'Falha ao salvar área',
        salvarStatus: 'Falha ao salvar status',
        renomearPasta: 'Falha ao renomear pasta',
        baixarZip: 'Erro ao baixar ZIP',
      },
      apreciacaoModal: {
        tituloNova: 'Nova apreciação',
        tituloExistentes: 'Apreciações da pasta',
        maquina: 'Máquina: {{nome}}',
        vinculadaUma: 'Esta pasta está vinculada a 1 apreciação.',
        vinculadaVarias: 'Esta pasta está vinculada a {{quantidade}} apreciações.',
        semNome: 'Apreciação',
        editarTitulo: 'Editar apreciação',
        removerVinculo: 'Remover este vínculo',
        vincularMais: 'Vincule a outras apreciações abaixo (sem perder estas) ou crie uma nova.',
        criarDoZero: 'Criar do zero',
        criarDoZeroDescricao: 'Apreciação em branco para preencher',
        modelosMaquina: 'Ou escolher um modelo de máquina',
        modeloDetalhe: '{{partes}} parte(s) · {{riscos}} risco(s)',
        modeloDetalheSufixo: ' · {{detalhe}}',
        templatesSimples: 'Ou escolher um template simples',
        semTemplates: 'Nenhum template simples neste projeto.',
        vincularExistentes: 'Ou vincular a apreciações existentes',
        buscarPlaceholder: 'Buscar por número, título ou máquina...',
        carregando: 'Carregando apreciações...',
        semApreciacoes: 'Nenhuma apreciação neste projeto.',
        semResultados: 'Nenhuma apreciação encontrada.',
        vincular: 'Vincular',
        processando: 'Processando...',
        erroVinculo: 'Falha ao atualizar vínculo',
        erroCriar: 'Falha ao criar apreciação',
        erroCriarGenerico: 'Erro ao criar apreciação',
        erroModelo: 'Apreciação criada, mas falha ao aplicar o modelo de máquina.',
      },
      video: {
        fechar: 'Fechar (Esc)',
        semSuporte:
          'O navegador não conseguiu reproduzir este vídeo. A captura de quadro é feita no servidor, então ainda funciona: use o tempo em segundos e clique em capturar.',
        quadroAnterior: 'Um quadro para trás',
        quadroProximo: 'Um quadro para frente',
        extraindo: 'Extraindo…',
        capturar: 'Capturar este quadro',
        dica: 'O quadro é salvo na pasta desta máquina, em resolução cheia, como foto normal.',
        salvos: '{{quantidade}} quadro(s) salvo(s):',
        erroExtrair: 'Não foi possível extrair o quadro.',
        erroExtrairGenerico: 'Erro ao extrair o quadro.',
      },
    },

    upload: {
      fotos: {
        erroSelecao: 'Selecione pelo menos uma imagem válida',
        erroEnvio: 'Falha ao enviar fotos',
      },
      zip: {
        botao: 'Importar ZIP',
        botaoTitulo: 'Enviar um ZIP com uma pasta por máquina (vai para "{{acervo}}")',
        enviando: 'Enviando {{percentual}}%',
        analisando: 'Analisando…',
        percentualEnviado: '{{percentual}}% enviado',
        lendoServidor: 'Lendo o conteúdo do ZIP no servidor…',
        confiraTitulo: 'Confira antes de importar — nada foi gravado ainda',
        confiraResumo: '{{fotos}} foto(s) em {{pastas}} pasta(s), indo para {{destaque}}.',
        confiraIgnorados: ' {{quantidade}} arquivo(s) ignorado(s) por não serem imagem.',
        pastaFotos: '{{quantidade}} foto(s)',
        conflitos: 'Serão ignoradas por já existirem no outro acervo: {{pastas}}',
        importando: 'Importando…',
        importar: 'Importar',
        descartar: 'Descartar',
        demora: 'A extração de um acervo grande leva alguns minutos.',
        resultado: '{{fotos}} foto(s) importada(s) em {{pastas}} pasta(s) — {{novas}} pasta(s) nova(s).',
        resultadoIgnorados: '{{quantidade}} arquivo(s) do ZIP ignorado(s) por não serem imagem.',
        resultadoConflitos: 'Ignoradas por já existirem no outro acervo: {{pastas}}',
        erroEnvio: 'Falha ao enviar o ZIP',
        erroEnvioGenerico: 'Erro ao enviar o ZIP',
        erroImportar: 'Falha ao importar o ZIP',
        erroImportarGenerico: 'Erro ao importar o ZIP',
      },
    },

    encaixe: {
      gerando: 'IA gerando… {{percentual}}%',
      emAndamento: 'Geração por IA em andamento',
      criar: 'Criar apreciação com IA',
      gerarNova: 'Gerar NOVA apreciação com IA',
      semFotos: 'Pasta sem fotos — envie fotos antes',
      foraEscopo: 'Pasta fora do escopo de apreciação',
      cartaoTitulo: 'Criar com IA (recomendado)',
      cartaoDescricao:
        'Analisa as {{fotos}} foto(s), resolve o contexto no cadastro e monta cabeçalho, C.1–C.4 e as partes. Você confirma a máquina antes.',
    },

    revisaoLegado: {
      enviarFotosMigradas: 'Enviar fotos migradas',
      confirmarMigracao:
        'Migrar "{{nome}}" para as pastas atuais?\n\n{{aprovadas}} foto(s) aprovada(s) vão junto.\n{{reprovadas}} reprovada(s) e {{pendentes}} pendente(s) ficam arquivadas no legado.',
      erroMigrar: 'Falha ao migrar pasta do legado',
      erroMigrarGenerico: 'Erro ao migrar pasta do legado',
      erroRevisar: 'Falha ao salvar a revisão da foto',
      veredito: {
        semFotos: 'Sem fotos',
        semFotosDetalhe: 'Envie as fotos antigas desta máquina para começar a revisão.',
        naoRevisada: 'Não revisada',
        naoRevisadaDetalhe: '{{total}} foto(s) aguardando revisão.',
        emRevisao: 'Em revisão · faltam {{pendentes}}',
        emRevisaoDetalhe: '{{aprovadas}} aprovada(s), {{reprovadas}} reprovada(s), {{pendentes}} pendente(s).',
        precisaMaisImagens: 'Precisa de mais imagens',
        precisaMaisImagensDetalhe:
          '{{reprovadas}} de {{total}} foto(s) reprovada(s) — é preciso voltar a campo.',
        aprovada: 'Aprovada',
        aprovadaDetalhe: 'As {{total}} foto(s) foram aprovadas.',
      },
      motivos: {
        tremida: 'Foto tremida ou escura',
        enquadramento: 'Enquadramento insuficiente',
        placa: 'Falta a placa de identificação',
        painel: 'Falta o painel elétrico',
        outraMaquina: 'Não é da máquina',
        duplicada: 'Duplicada',
      },
      card: {
        selo: 'Legado',
        abrir: 'Entrar na máquina e revisar as fotos',
        semFotos: 'Sem fotos migradas ainda',
        resumo: '{{total}} foto(s) · {{aprovadas}} aprovada(s) · {{reprovadas}} reprovada(s)',
        resumoPendentes: ' · {{pendentes}} a revisar',
        revisarTitulo: 'Entrar na máquina para aprovar/reprovar as fotos',
        revisar: 'Revisar',
        revisarPendentes: 'Revisar ({{pendentes}})',
        migrar: 'Migrar',
        migrarBloqueado: 'Aprove ao menos uma foto para migrar',
        migrarTitulo: 'Migrar {{aprovadas}} foto(s) aprovada(s) para as pastas atuais',
      },
      metricas: {
        revisado: '({{percentual}}% revisado)',
        maquinas: 'Máquinas no legado',
        aprovadas: 'Máquinas aprovadas · {{fotos}} foto(s)',
        precisamImagens: 'Precisam de mais imagens · {{fotos}} foto(s) reprovada(s)',
        aRevisar: 'Fotos a revisar em {{maquinas}} máquina(s)',
      },
      vazio: {
        semPendentes: 'Nenhuma máquina do legado com fotos pendentes — a revisão está em dia.',
        semMaquinas:
          'Nenhuma máquina no legado ainda. Crie a máquina no campo acima e envie as fotos antigas para começar a revisão.',
        semResultados: 'Nenhuma máquina do legado encontrada para esta busca.',
      },
      modal: {
        resumo:
          '{{total}} foto(s) · {{aprovadas}} aprovada(s) · {{reprovadas}} reprovada(s) · {{pendentes}} pendente(s)',
        preCarga: 'adiantando vídeos curtos {{prontos}}/{{total}}',
        preCargaPronta: '{{total}} vídeo(s) curto(s) prontos',
        proximaPendente: 'Próxima pendente',
        proximaPendenteTitulo: 'Ir para a próxima foto sem veredito',
        adicionarFotos: 'Adicionar fotos',
        adicionarFotosTitulo: 'Enviar mais fotos para esta máquina',
        fechar: 'Fechar (Esc)',
        semFotos: 'Esta máquina do legado ainda não tem fotos.',
        fotoAnterior: 'Foto anterior (←)',
        proximaFoto: 'Próxima foto (→)',
        aprovada: 'Aprovada',
        reprovada: 'Reprovada',
        motivoSufixo: ' · {{motivo}}',
        porQueReprovada: 'Por que esta foto foi reprovada?',
        outroMotivo: 'Outro motivo (opcional)...',
        aprovar: 'Aprovar',
        aprovarTitulo: 'Aprovar esta foto (A)',
        reprovar: 'Reprovar',
        reprovarTitulo: 'Reprovar esta foto (R)',
        desfazer: 'Desfazer',
        desfazerTitulo: 'Desfazer o veredito desta foto',
        apagar: 'Apagar',
        apagarTitulo: 'Apagar esta foto do acervo',
        atalhos: 'Atalhos: A aprova · R reprova · ← → navegam · Esc fecha',
        erroSalvar: 'Não foi possível salvar o veredito desta foto.',
      },
    },
  },

  en: {
    acervo: {
      titulo: 'Machine Images',
      subtitulo: "Organize photos by area and track each machine's assessment status.",
      subtituloLegado:
        'Migrated archive: open the machine and approve or reject each photo to find out whether another field visit is needed.',
      semProjeto: 'Select a project to view the image uploads.',
      semArea: 'No area',
      visao: {
        porArea: 'By area',
        porAreaTitulo: 'Group by area',
        lista: 'List',
        listaTitulo: 'Show every folder as a list',
      },
      aba: {
        atuais: 'Current',
        legado: 'Legacy',
        legadoTitulo: 'Old migrated photos, under photo-by-photo review',
      },
      busca: {
        placeholder: 'Search folder, machine, area or status...',
        novaPasta: 'New folder...',
        novaMaquinaLegado: 'New legacy machine...',
      },
      filtro: {
        pendentes: 'Pending',
        pendentesTitulo:
          "Show only folders with no assessment and no 'To do' status (cancelled and non-NR12 are left out)",
        aRevisar: 'To review',
        aRevisarTitulo: 'Show only machines with photos still awaiting a verdict',
      },
      metricas: {
        totalMaquinas: 'Total machines',
        canceladasSufixo: ' · {{quantidade}} cancelled',
        naoNr12Sufixo: ' · {{quantidade}} not NR12',
        comApreciacao: 'With assessment',
        semApreciacao: 'Without assessment',
      },
      grupo: {
        maquinas: '{{quantidade}} machine(s)',
      },
      vazio: {
        semPastas: 'No folder with images in this project.',
        semPendentes:
          'No pending folder — every one of them has an assessment, is marked as "To do" or is out of scope.',
      },
      semProjetoLista: {
        titulo: 'Folders without a project',
        descricao: 'Old folders found outside the per-project split.',
      },
      status: {
        aFazer: 'To do',
        semFotoMaquina: 'No machine photo',
        semFotoPainel: 'No panel photo',
        incompleto: 'Incomplete',
        naoNr12: 'Not NR12',
        cancelado: 'Cancelled',
        definir: 'Set the folder status',
        alterar: 'Status: {{status}} (click to change)',
        remover: 'Remove status',
      },
      levantamento: {
        rotulo: 'Field survey: {{data}}',
        captura: 'Date recorded by the app when the photo was taken',
        upload: "Date the folder's first photo reached the server",
        inferida: 'Estimated from the oldest file in the folder — it may lag the visit by days',
        importacao: 'Imported archive folder: this is the import date, not the survey date',
      },
      card: {
        fotos: '{{quantidade}} photo(s)',
        videosSufixo: ' · {{quantidade}} video(s)',
        semFotos: 'No photos yet',
        renomear: 'Rename folder',
        renomearPlaceholder: 'New folder name...',
        salvarNome: 'Save name',
        definirArea: 'Set area',
        areaPlaceholder: 'Area name...',
        salvarArea: 'Save area',
        adicionarFotos: 'Add photos',
        baixarZip: 'Download the folder as ZIP',
        pastaSemFotos: 'Folder has no photos',
        apagarPasta: 'Delete folder',
        apreciacao: 'Assessment',
        semApreciacao: 'No assessment',
        variasApreciacoes: '{{quantidade}} assessments',
        editarApreciacao: 'Edit assessment',
        gerenciarApreciacoes: 'View/manage assessments',
        criarApreciacao: 'Create assessment',
        vincularApreciacao: 'Link assessment',
        alterarApreciacao: 'Change/unlink assessment',
      },
      galeria: {
        vazioTitulo: 'This folder has no photos yet.',
        vazioDica: 'Use the add photos button on the folder card to upload images.',
        abrirVideo: 'Open the video and capture a frame',
        abrirFoto: 'Open in high quality',
        apagarFoto: 'Delete photo',
      },
      confirmar: {
        apagarPasta: 'Delete folder "{{nome}}" with {{fotos}} photo(s)? This action cannot be undone.',
        apagarFoto: 'Delete photo "{{nome}}"? This action cannot be undone.',
      },
      erro: {
        carregar: 'Could not load the images',
        semProjeto: 'No project selected.',
        criarPasta: 'Could not create the folder',
        apagarPasta: 'Could not delete the folder',
        apagarFoto: 'Could not delete the photo',
        salvarArea: 'Could not save the area',
        salvarStatus: 'Could not save the status',
        renomearPasta: 'Could not rename the folder',
        baixarZip: 'Error downloading the ZIP',
      },
      apreciacaoModal: {
        tituloNova: 'New assessment',
        tituloExistentes: 'Folder assessments',
        maquina: 'Machine: {{nome}}',
        vinculadaUma: 'This folder is linked to 1 assessment.',
        vinculadaVarias: 'This folder is linked to {{quantidade}} assessments.',
        semNome: 'Assessment',
        editarTitulo: 'Edit assessment',
        removerVinculo: 'Remove this link',
        vincularMais: 'Link to other assessments below (without losing these) or create a new one.',
        criarDoZero: 'Create from scratch',
        criarDoZeroDescricao: 'A blank assessment for you to fill in',
        modelosMaquina: 'Or pick a machine template',
        modeloDetalhe: '{{partes}} part(s) · {{riscos}} hazard(s)',
        modeloDetalheSufixo: ' · {{detalhe}}',
        templatesSimples: 'Or pick a simple template',
        semTemplates: 'No simple template in this project.',
        vincularExistentes: 'Or link to existing assessments',
        buscarPlaceholder: 'Search by number, title or machine...',
        carregando: 'Loading assessments...',
        semApreciacoes: 'No assessment in this project.',
        semResultados: 'No assessment found.',
        vincular: 'Link',
        processando: 'Processing...',
        erroVinculo: 'Could not update the link',
        erroCriar: 'Could not create the assessment',
        erroCriarGenerico: 'Error creating the assessment',
        erroModelo: 'Assessment created, but the machine template could not be applied.',
      },
      video: {
        fechar: 'Close (Esc)',
        semSuporte:
          'Your browser could not play this video. Frame capture runs on the server, so it still works: use the time in seconds and click capture.',
        quadroAnterior: 'One frame back',
        quadroProximo: 'One frame forward',
        extraindo: 'Extracting…',
        capturar: 'Capture this frame',
        dica: "The frame is saved in this machine's folder, at full resolution, as a regular photo.",
        salvos: '{{quantidade}} frame(s) saved:',
        erroExtrair: 'The frame could not be extracted.',
        erroExtrairGenerico: 'Error extracting the frame.',
      },
    },

    upload: {
      fotos: {
        erroSelecao: 'Select at least one valid image',
        erroEnvio: 'Could not upload the photos',
      },
      zip: {
        botao: 'Import ZIP',
        botaoTitulo: 'Upload a ZIP with one folder per machine (goes to "{{acervo}}")',
        enviando: 'Uploading {{percentual}}%',
        analisando: 'Analyzing…',
        percentualEnviado: '{{percentual}}% uploaded',
        lendoServidor: 'Reading the ZIP contents on the server…',
        confiraTitulo: 'Check before importing — nothing has been saved yet',
        confiraResumo: '{{fotos}} photo(s) in {{pastas}} folder(s), going to {{destaque}}.',
        confiraIgnorados: ' {{quantidade}} file(s) skipped for not being images.',
        pastaFotos: '{{quantidade}} photo(s)',
        conflitos: 'These will be skipped because they already exist in the other archive: {{pastas}}',
        importando: 'Importing…',
        importar: 'Import',
        descartar: 'Discard',
        demora: 'Extracting a large archive takes a few minutes.',
        resultado: '{{fotos}} photo(s) imported into {{pastas}} folder(s) — {{novas}} new folder(s).',
        resultadoIgnorados: '{{quantidade}} file(s) in the ZIP skipped for not being images.',
        resultadoConflitos: 'Skipped because they already exist in the other archive: {{pastas}}',
        erroEnvio: 'Could not upload the ZIP',
        erroEnvioGenerico: 'Error uploading the ZIP',
        erroImportar: 'Could not import the ZIP',
        erroImportarGenerico: 'Error importing the ZIP',
      },
    },

    encaixe: {
      gerando: 'AI generating… {{percentual}}%',
      emAndamento: 'AI generation in progress',
      criar: 'Create assessment with AI',
      gerarNova: 'Generate a NEW assessment with AI',
      semFotos: 'Folder has no photos — upload photos first',
      foraEscopo: 'Folder is out of assessment scope',
      cartaoTitulo: 'Create with AI (recommended)',
      cartaoDescricao:
        'Analyzes the {{fotos}} photo(s), resolves the context from the records and builds the header, C.1–C.4 and the parts. You confirm the machine first.',
    },

    revisaoLegado: {
      enviarFotosMigradas: 'Upload migrated photos',
      confirmarMigracao:
        'Migrate "{{nome}}" to the current folders?\n\n{{aprovadas}} approved photo(s) come along.\n{{reprovadas}} rejected and {{pendentes}} pending stay archived in the legacy tab.',
      erroMigrar: 'Could not migrate the legacy folder',
      erroMigrarGenerico: 'Error migrating the legacy folder',
      erroRevisar: 'Could not save the photo review',
      veredito: {
        semFotos: 'No photos',
        semFotosDetalhe: "Upload this machine's old photos to start the review.",
        naoRevisada: 'Not reviewed',
        naoRevisadaDetalhe: '{{total}} photo(s) awaiting review.',
        emRevisao: 'Under review · {{pendentes}} left',
        emRevisaoDetalhe: '{{aprovadas}} approved, {{reprovadas}} rejected, {{pendentes}} pending.',
        precisaMaisImagens: 'Needs more images',
        precisaMaisImagensDetalhe:
          '{{reprovadas}} of {{total}} photo(s) rejected — another field visit is needed.',
        aprovada: 'Approved',
        aprovadaDetalhe: 'All {{total}} photo(s) were approved.',
      },
      motivos: {
        tremida: 'Blurry or dark photo',
        enquadramento: 'Framing is not wide enough',
        placa: 'Nameplate is missing',
        painel: 'Electrical panel is missing',
        outraMaquina: 'Not of this machine',
        duplicada: 'Duplicate',
      },
      card: {
        selo: 'Legacy',
        abrir: 'Open the machine and review the photos',
        semFotos: 'No migrated photos yet',
        resumo: '{{total}} photo(s) · {{aprovadas}} approved · {{reprovadas}} rejected',
        resumoPendentes: ' · {{pendentes}} to review',
        revisarTitulo: 'Open the machine to approve/reject the photos',
        revisar: 'Review',
        revisarPendentes: 'Review ({{pendentes}})',
        migrar: 'Migrate',
        migrarBloqueado: 'Approve at least one photo to migrate',
        migrarTitulo: 'Migrate {{aprovadas}} approved photo(s) to the current folders',
      },
      metricas: {
        revisado: '({{percentual}}% reviewed)',
        maquinas: 'Machines in the legacy archive',
        aprovadas: 'Approved machines · {{fotos}} photo(s)',
        precisamImagens: 'Need more images · {{fotos}} rejected photo(s)',
        aRevisar: 'Photos to review across {{maquinas}} machine(s)',
      },
      vazio: {
        semPendentes: 'No legacy machine with pending photos — the review is up to date.',
        semMaquinas:
          'No legacy machine yet. Create the machine in the field above and upload the old photos to start the review.',
        semResultados: 'No legacy machine found for this search.',
      },
      modal: {
        resumo: '{{total}} photo(s) · {{aprovadas}} approved · {{reprovadas}} rejected · {{pendentes}} pending',
        preCarga: 'preloading short videos {{prontos}}/{{total}}',
        preCargaPronta: '{{total}} short video(s) ready',
        proximaPendente: 'Next pending',
        proximaPendenteTitulo: 'Go to the next photo without a verdict',
        adicionarFotos: 'Add photos',
        adicionarFotosTitulo: 'Upload more photos for this machine',
        fechar: 'Close (Esc)',
        semFotos: 'This legacy machine has no photos yet.',
        fotoAnterior: 'Previous photo (←)',
        proximaFoto: 'Next photo (→)',
        aprovada: 'Approved',
        reprovada: 'Rejected',
        motivoSufixo: ' · {{motivo}}',
        porQueReprovada: 'Why was this photo rejected?',
        outroMotivo: 'Other reason (optional)...',
        aprovar: 'Approve',
        aprovarTitulo: 'Approve this photo (A)',
        reprovar: 'Reject',
        reprovarTitulo: 'Reject this photo (R)',
        desfazer: 'Undo',
        desfazerTitulo: "Undo this photo's verdict",
        apagar: 'Delete',
        apagarTitulo: 'Delete this photo from the archive',
        atalhos: 'Shortcuts: A approves · R rejects · ← → navigate · Esc closes',
        erroSalvar: "This photo's verdict could not be saved.",
      },
    },
  },
};
